import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toTitleCase } from "@/lib/text-utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Database, Loader2, Plus } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import QueryEditor from "./QueryEditor";

interface SavedQuery {
  id: string;
  name: string;
  sql_query: string;
  generation_mode: string;
  updated_at: string;
  pipeline_source_connector?: { name: string } | null;
}

interface QueryPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (query: { id: string; name: string; sql_query: string }) => void;
  /** When provided, new queries created from this dialog will be linked to the given entity. */
  linkType?: string;
  linkId?: string;
}

const QueryPickerDialog = ({ open, onOpenChange, onSelect, linkType, linkId }: QueryPickerDialogProps) => {
  const [queries, setQueries] = useState<SavedQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState<"list" | "editor">("list");
  const { tables: schemaTables, columns: schemaColumns } = useMcpSchema();

  const fetchQueries = useCallback(() => {
    setLoading(true);
    supabase
      .from("saved_queries" as any)
      .select("id, name, sql_query, generation_mode, updated_at, pipeline_source_connector(name)")
      .order("updated_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setQueries(data as unknown as SavedQuery[]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) {
      // Reset to list view when dialog closes
      setView("list");
      return;
    }
    setFilter("");
    fetchQueries();
  }, [open, fetchQueries]);

  const filtered = queries.filter((q) =>
    q.name.toLowerCase().includes(filter.toLowerCase())
  );

  const modeBadgeVariant = (mode: string) => {
    switch (mode) {
      case "builder": return "default";
      case "ai": return "secondary";
      default: return "outline";
    }
  };

  const handleEditorClose = () => {
    setView("list");
    fetchQueries();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col p-0 gap-0",
          view === "editor"
            ? "sm:max-w-6xl h-[90vh]"
            : "sm:max-w-2xl max-h-[80vh]"
        )}
      >
        {view === "list" ? (
          <>
            <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Database className="h-4 w-4 text-indigo-600" />
                Select a Query
              </DialogTitle>
            </DialogHeader>

            <div className="px-6 pt-3 pb-2 shrink-0">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search by name…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="pl-9"
                    autoFocus
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setView("editor")}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> New Query
                </Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Database className="h-8 w-8 mb-2" />
                  <p className="text-sm">{filter ? "No queries match your search." : "No saved queries yet."}</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2 text-left font-medium">Name</th>
                        <th className="px-4 py-2 text-left font-medium">Database</th>
                        <th className="px-4 py-2 text-left font-medium">Mode</th>
                        <th className="px-4 py-2 text-left font-medium">Updated</th>
                        <th className="px-4 py-2 text-right font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((q) => (
                        <tr key={q.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="px-4 py-2 font-medium">{toTitleCase(q.name)}</td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {q.pipeline_source_connector?.name ?? "—"}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={modeBadgeVariant(q.generation_mode) as any}>
                              {q.generation_mode}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {format(new Date(q.updated_at), "MMM d, yyyy")}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                              onClick={() => {
                                onSelect({ id: q.id, name: q.name, sql_query: q.sql_query });
                                onOpenChange(false);
                              }}
                            >
                              Use this query
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <QueryEditor
            onClose={handleEditorClose}
            editingQuery={null}
            schemaTables={schemaTables}
            schemaColumns={schemaColumns}
            linkType={linkType}
            linkId={linkId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QueryPickerDialog;
